import { ChangeDetectorRef, Component, Injector, OnDestroy } from '@angular/core';
import { MessageReportsService } from 'src/app/_services/messaging/messages/message-reports.service';
import {
  EmailMessageReportsRetrievingModel
} from 'src/app/_models/messaging/messages/message-reports/email-reports/email-message-reports-retrieving-model';
import {
  MessageReportGridViewModelAdapter
} from 'src/app/_models/messaging/messages/message-reports/email-reports/message-report-grid-view-model.adapter';
import {
  MessageReportGridViewModel
} from 'src/app/_models/messaging/messages/message-reports/email-reports/message-report-grid-view-model';
import { DateService } from 'src/app/_services/system/date.service';
import { HelperService } from 'src/app/_services/system/helpers/helper.service';
import { MessagingAbstract } from '../../../../_models/messaging/messaging-abstract';
import { MessageFolderService } from '../../../../_services/messaging/messages/message-folder.service';
import { ExecutionResult } from '../../../../_models/execution-result-enum';
import { ExecutionResultDto } from '../../../../_models/execution-result-model';
import { TableActionType } from '../../../shared/constants/table-constants';
import { DataTypesEnum } from 'src/app/_models/selected-fields-filters/data-types-enum';
import { PagerModel } from 'src/app/_models/system/pager-model';

@Component({
  selector: 'app-message-reports',
  templateUrl: './message-reports.component.html',
  styleUrls: ['./message-reports.component.scss'],
})
export class MessageReportsComponent extends MessagingAbstract implements OnDestroy {
  displayedColumns = [
    { name: 'listId', displayName: 'List ID' },
    { name: 'messageName', displayName: 'Message Name' },
    { name: 'messageSourceName', displayName: 'Message Source Name' },
    { name: 'sentBy', displayName: 'Sent By' },
    { name: 'folderName', displayName: 'Folder Name' },
    { name: 'lastSentDate', displayName: 'Last Sent Date' },
    { name: 'totalSent', displayName: 'Total Sent' },
    { name: 'totalOpens', displayName: 'Total Opens' },
    { name: 'openRate', displayName: 'Open Rate (%)' },
    { name: 'totalBounces', displayName: 'Total Bounces' },
    { name: 'bounceRate', displayName: 'Bounce Rate (%)' },
    { name: 'totalClicks', displayName: 'Total Clicks' },
    { name: 'clickThroughRate', displayName: 'Click Through Rate (%)' },
    { name: 'actions', displayName: '' }
  ];

  emailReportModel: EmailMessageReportsRetrievingModel;
  dataType = DataTypesEnum.EmailReports;

  _exportCancelled = false;
  _exporting = false;
  _exportPercent = 0;
  _exportMessage = '';

  private _listSortColumn = 'ChangeDate';
  private _listSortDirection = 'Descending';
  private static readonly exportBeforeDownloadDelayMs = 280;

  constructor(
    private messageReportGridViewModelAdapter: MessageReportGridViewModelAdapter,
    private dateService: DateService,
    private helperService: HelperService,
    public messageFolderService: MessageFolderService,
    public messageReportsService: MessageReportsService,
    injector: Injector,
    private cdr: ChangeDetectorRef
  ) {
    super(injector);
    this.tableKey = 'emailReports';
    this.init();
  }

  ngOnDestroy(): void {
    this._exportCancelled = true;
  }

  getReports(data): void {
    if (data?.sortColumn) {
      this._listSortColumn = data.sortColumn;
    }
    if (data?.sortDirection) {
      this._listSortDirection = data.sortDirection;
    }

    this.loadingSpinnerService.loading();
    this.emailReportModel = {
      PagerModel: {
        Page: data.currentPage,
        PageSize: data.pageLimit,
        SortColumn: data.sortColumn,
        SortDirection: data.sortDirection
      },
      FolderID: this.selectedFolderId
    };

    this.messageReportsService.getEmailReports(this.emailReportModel).subscribe((executionResultDto: ExecutionResultDto) => {
      if (executionResultDto.executionResult === ExecutionResult.success) {
        this.data = this.messageReportGridViewModelAdapter.adaptArray(executionResultDto.data.Item1);
        this.length = executionResultDto.data.Item2;
        this.formatDate();
        this.roundRates();
        this.loadingSpinnerService.stopLoading();
      } else {
        this.notificationService.showError(executionResultDto.message);
        this.loadingSpinnerService.stopLoading();
      }
    });
  }

  formatDate(): void {
    this.data.forEach((report: MessageReportGridViewModel) => {
      report.lastSentDate = this.dateService.formatStringDate(report.lastSentDate);
    });
  }

  formatDateRows(rows: MessageReportGridViewModel[]): void {
    rows.forEach((report: MessageReportGridViewModel) => {
      report.lastSentDate = this.dateService.formatStringDate(report.lastSentDate);
    });
  }

  roundRates(): void {
    this.data.forEach((report: MessageReportGridViewModel) => {
      report.openRate = (+report.openRate * 100).toFixed(0);
      report.bounceRate = (+report.bounceRate * 100).toFixed(0);
      report.clickThroughRate = (+report.clickThroughRate * 100).toFixed(0);
    });
  }

  roundRatesRows(rows: MessageReportGridViewModel[]): void {
    rows.forEach((report: MessageReportGridViewModel) => {
      report.openRate = (+report.openRate * 100).toFixed(0);
      report.bounceRate = (+report.bounceRate * 100).toFixed(0);
      report.clickThroughRate = (+report.clickThroughRate * 100).toFixed(0);
    });
  }

  actionHandler({ selectedRows, action }): void {
    switch (TableActionType[action]) {
      case TableActionType.openDetails:
        this.router.navigate([ 'email-reports/details/', selectedRows.reportId]);
        break;
      case TableActionType.export:
        this.messageReportsService.export(this.data, selectedRows.map(row => {
          return {
            displayName: row,
            name: this.displayedColumns.find(x => x.displayName === row)?.name
          }
        }));
        break;
      case TableActionType.exportAllView:
        this.exportAllViewChunked(Array.isArray(selectedRows) ? selectedRows : []);
        break;
    }
  }

  private exportAllViewChunked(columnDisplayNames: string[]): void {
    const total = this.length;
    if (!total || total < 1) {
      this.notificationService.showWarning('No data to export.');
      return;
    }

    const safeColumns = (columnDisplayNames ?? []).filter((c) => c && String(c).trim().length > 0);
    if (safeColumns.length < 1) {
      this.notificationService.showWarning('Select at least one column to export.');
      return;
    }

    if (this._exporting) {
      this.notificationService.showWarning('An export is already in progress.');
      return;
    }

    const mapped = safeColumns.map((row) => ({
      displayName: row,
      name: this.displayedColumns.find((x) => x.displayName === row)?.name
    }));
    mapped.pop();
    const exportCols = mapped.filter((c) => c.name);
    if (exportCols.length < 1) {
      this.notificationService.showWarning('Select at least one column to export.');
      return;
    }

    this._exportCancelled = false;
    this._exporting = true;
    this._exportPercent = 0;
    this._exportMessage = 'About 0% loaded';
    this.cdr.detectChanges();

    const chunkSize = 5000;
    const totalChunkRequests = Math.max(1, Math.ceil(total / chunkSize));
    const maxPages = totalChunkRequests + 50;
    const allRecords: MessageReportGridViewModel[] = [];
    let page = 1;

    let totalTarget = total;
    const progressTick = (loaded: number) => {
      const t = totalTarget;
      let pct = 0;
      if (t > 0 && loaded > 0) {
        pct = loaded >= t ? 100 : Math.min(99, Math.max(1, Math.ceil((loaded / t) * 100)));
      }
      this._exportPercent = pct;
      this._exportMessage =
        t > 0 && loaded > 0
          ? `About ${pct}% loaded (${loaded.toLocaleString()} / ${t.toLocaleString()} rows)`
          : 'About 0% loaded';
      this.cdr.detectChanges();
    };

    const finishExport = (onFinally?: () => void) => {
      this._exporting = false;
      this._exportPercent = 0;
      this._exportMessage = '';
      this.cdr.detectChanges();
      onFinally?.();
    };

    const writeCsv = (rows: MessageReportGridViewModel[]) => {
      const cap = totalTarget;
      const capped = rows.length > cap ? rows.slice(0, cap) : rows;
      const csvRows = capped.map((row) => {
        const exportRow: { [key: string]: any } = {};
        exportCols.forEach((col) => {
          exportRow[col.displayName] = row[col.name];
        });
        return exportRow;
      });
      const today = new Date();
      const fileName = `EmailReport_${today.getDate()}${today.getMonth() + 1}${today.getFullYear()}`;
      this.helperService.exportToCSV(fileName, csvRows);
      this.helperService.sentEmailWarningNotificationOfFileDownload('Email Report', today);
    };

    const tryWriteCsvAndFinish = (rows: MessageReportGridViewModel[], onFinally?: () => void) => {
      progressTick(rows.length);
      const doneMsg = 'About 100% — preparing download';
      this._exportPercent = 100;
      this._exportMessage = doneMsg;
      this.cdr.detectChanges();
      const runDownload = () => {
        try {
          writeCsv(rows);
          finishExport(onFinally);
        } catch {
          finishExport(() =>
            this.notificationService.showError('Could not build or download the CSV file. Please try again.')
          );
        }
      };
      setTimeout(runDownload, MessageReportsComponent.exportBeforeDownloadDelayMs);
    };

    const fetchNext = () => {
      if (this._exportCancelled) {
        finishExport();
        return;
      }

      if (page > maxPages) {
        if (allRecords.length > 0) {
          tryWriteCsvAndFinish(allRecords, () =>
            this.notificationService.showWarning(
              `Export stopped after ${maxPages} page requests. Saved ${allRecords.length.toLocaleString()} rows (total was ${totalTarget.toLocaleString()}).`
            )
          );
        } else {
          finishExport(() =>
            this.notificationService.showError(
              'Export stopped: too many empty pages. Try again or contact support.'
            )
          );
        }
        return;
      }

      const reportModel = new EmailMessageReportsRetrievingModel({
        FolderID: this.selectedFolderId,
        PagerModel: new PagerModel({
          Page: page,
          PageSize: chunkSize,
          SortColumn: this._listSortColumn,
          SortDirection: this._listSortDirection
        })
      });

      this.messageReportsService.getEmailReports(reportModel).subscribe({
        next: (executionResultDto: ExecutionResultDto) => {
          if (this._exportCancelled) {
            finishExport();
            return;
          }

          if (executionResultDto.executionResult !== ExecutionResult.success) {
            finishExport(() =>
              this.notificationService.showError(executionResultDto.message || 'The export request failed.')
            );
            return;
          }

          if (!executionResultDto.data) {
            finishExport(() => this.notificationService.showError('Invalid response from server.'));
            return;
          }

          const d = executionResultDto.data;
          const apiN = Number(d.Item2);
          if (Number.isFinite(apiN) && apiN > 0) {
            totalTarget = Math.max(totalTarget, apiN);
          }
          const raw = d.Item1 ?? [];
          const chunk = this.messageReportGridViewModelAdapter.adaptArray(raw);
          this.formatDateRows(chunk);
          this.roundRatesRows(chunk);
          allRecords.push(...chunk);
          progressTick(allRecords.length);

          if (chunk.length === 0) {
            if (allRecords.length === 0) {
              finishExport(() => this.notificationService.showError('No rows returned for export.'));
            } else {
              tryWriteCsvAndFinish(allRecords);
            }
            return;
          }

          const lastPage = chunk.length < chunkSize;
          const reachedTotal = allRecords.length >= totalTarget;
          if (reachedTotal || lastPage) {
            tryWriteCsvAndFinish(allRecords);
            return;
          }

          page += 1;
          fetchNext();
        },
        error: () => {
          finishExport(() => this.notificationService.showError('Could not export the report. Please try again.'));
        }
      });
    };

    fetchNext();
  }
}
